import React, { useRef, useState } from "react";
import { Button } from "primereact/button";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase/firebase";
import { toast } from "react-toastify";
import axios from "axios";
import { DEV } from "../../services/constants";
import useViewOnly from "../../hooks/useViewOnly";
import styles from "./lowConnectivityCapture.module.scss";

const initials = (name) =>
    (name || "")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join("") || "?";

/**
 * Low-connectivity handover capture: no live lookups, no signature - just a
 * typed receiver name and label photos per receiver. Package identity and the
 * actual registered client are resolved afterward via background OCR, not
 * here, so this step deliberately stays offline-friendly.
 */
const LowConnectivityCapture = ({ handoverId, onSubmitted }) => {
    const [receiverMode, setReceiverMode] = useState(null); // "single" | "multiple"
    const [entries, setEntries] = useState([]);
    const [currentName, setCurrentName] = useState("");
    const [currentPhotos, setCurrentPhotos] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const fileInputRef = useRef(null);
    const viewOnly = useViewOnly();

    const handleFiles = (files) => {
        const readers = Array.from(files).map(
            (file) =>
                new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.readAsDataURL(file);
                }),
        );
        Promise.all(readers).then((dataUrls) => setCurrentPhotos((prev) => [...prev, ...dataUrls]));
    };

    const removePhoto = (index) => {
        setCurrentPhotos((prev) => prev.filter((_, i) => i !== index));
    };

    const resetCurrent = () => {
        setCurrentName("");
        setCurrentPhotos([]);
    };

    const addEntry = () => {
        if (!currentName.trim()) {
            toast.error("Enter the receiver's name");
            return;
        }
        if (currentPhotos.length === 0) {
            toast.error("Add at least one photo");
            return;
        }
        setEntries((prev) => [...prev, { name: currentName.trim(), photos: currentPhotos }]);
        resetCurrent();
    };

    const uploadEntry = async (entry, index) => {
        const timestamp = Date.now();
        const photoUrls = await Promise.all(
            entry.photos.map(async (dataUrl, i) => {
                const blob = await fetch(dataUrl).then((r) => r.blob());
                const storageRef = ref(storage, `airportHandovers/${handoverId}/lowConnectivity/${timestamp}_${index}_${i}.jpg`);
                await uploadBytes(storageRef, blob);
                return getDownloadURL(storageRef);
            }),
        );
        return { receiver: { type: "guest", name: entry.name }, photoUrls };
    };

    const submitAll = async (finalEntries) => {
        if (finalEntries.length === 0) {
            toast.error("Add at least one receiver before submitting");
            return;
        }
        setSubmitting(true);
        try {
            const receivers = await Promise.all(finalEntries.map((entry, i) => uploadEntry(entry, i)));
            const response = await axios.post(
                `${DEV}/airport-handover/${handoverId}/images`,
                { receiverMode, receivers },
                { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
            );

            if (response?.data?.message === "Invalid token") {
                toast.error("session expired");
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }

            if (response?.data?.success) {
                toast.success("Images submitted - processing in the background");
                setEntries([]);
                resetCurrent();
                if (onSubmitted) onSubmitted();
            } else {
                toast.error(response?.data?.message || "Failed to submit images");
            }
        } catch (error) {
            console.error("Error submitting low-connectivity images:", error);
            toast.error(error?.response?.data?.message || "Failed to submit images");
            if (error?.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (!receiverMode) {
        return (
            <div className={styles.intro}>
                <div className={styles.introIcon}>
                    <i className="pi pi-users" />
                </div>
                <p>Are these packages being received by one person or multiple people?</p>
                <div className={styles.modeChoice}>
                    <div className={styles.modeOption} onClick={() => setReceiverMode("single")}>
                        <div className={styles.optionIcon}>
                            <i className="pi pi-user" />
                        </div>
                        <strong>Single Receiver</strong>
                        <span>One person receiving all packages</span>
                    </div>
                    <div className={styles.modeOption} onClick={() => setReceiverMode("multiple")}>
                        <div className={styles.optionIcon}>
                            <i className="pi pi-users" />
                        </div>
                        <strong>Multiple Receivers</strong>
                        <span>Capture each receiver separately</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <span
                className={styles.changeMode}
                onClick={() => {
                    setReceiverMode(null);
                    setEntries([]);
                    resetCurrent();
                }}
            >
                <i className="pi pi-arrow-left" />
                Change receiver mode
            </span>

            <div className={styles.field}>
                <label>Receiver Name</label>
                <input value={currentName} onChange={(e) => setCurrentName(e.target.value)} placeholder="Type the receiver's name" />
            </div>

            <div className={styles.field}>
                <label>
                    Label Photos <span className={styles.photoCount}>({currentPhotos.length})</span>
                </label>
                <div className={styles.dropzone} onClick={() => fileInputRef.current?.click()}>
                    <i className="pi pi-camera" />
                    <strong>Add Photos</strong>
                    <span>Tap to capture or upload label photos</span>
                </div>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    multiple
                    style={{ display: "none" }}
                    onChange={(e) => {
                        handleFiles(e.target.files);
                        e.target.value = "";
                    }}
                />
                {currentPhotos.length > 0 && (
                    <div className={styles.photoGrid}>
                        {currentPhotos.map((p, i) => (
                            <div key={i} className={styles.photoThumb}>
                                <img src={p} alt={`Label ${i + 1}`} />
                                <button type="button" onClick={() => removePhoto(i)}>
                                    <i className="pi pi-times" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {receiverMode === "multiple" ? (
                <>
                    <div className={styles.actionsRow}>
                        <Button label="Add Receiver" icon="pi pi-plus" outlined className={styles.primaryAction} onClick={addEntry} />
                        <Button
                            label={submitting ? "Submitting..." : `Done (${entries.length} receiver${entries.length === 1 ? "" : "s"})`}
                            icon={submitting ? "pi pi-spin pi-spinner" : "pi pi-check"}
                            className={styles.submitAction}
                            disabled={submitting || viewOnly}
                            onClick={() => {
                                const finalEntries = currentName.trim() && currentPhotos.length ? [...entries, { name: currentName.trim(), photos: currentPhotos }] : entries;
                                submitAll(finalEntries);
                            }}
                        />
                    </div>
                    {entries.length > 0 && (
                        <div className={styles.entriesList}>
                            <h5>Added Receivers</h5>
                            {entries.map((e, i) => (
                                <div key={i} className={styles.entryItem}>
                                    <div className={styles.entryAvatar}>{initials(e.name)}</div>
                                    <span className={styles.entryName}>{e.name}</span>
                                    <span className={styles.entryPhotos}>
                                        <i className="pi pi-camera" />
                                        {e.photos.length}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            ) : (
                <div className={styles.actionsRow}>
                    <Button
                        label={submitting ? "Submitting..." : "Submit"}
                        icon={submitting ? "pi pi-spin pi-spinner" : "pi pi-check"}
                        className={styles.submitAction}
                        disabled={submitting}
                        onClick={() => {
                            if (!currentName.trim() || currentPhotos.length === 0) {
                                toast.error("Enter a name and add at least one photo");
                                return;
                            }
                            submitAll([{ name: currentName.trim(), photos: currentPhotos }]);
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default LowConnectivityCapture;
